"use client";
import { useState } from "react";

import { Link } from "@heroui/link";
import { z } from "zod";

import { PageFormProps } from "../components/formInterfaces";
import { PageFormTemplate } from "../components/PageFormTemplate";

import { OrganizationSearch } from "./OrganizationSearch";

import { FormInput } from "@/components/forms/components/FormInputs";
import { registerUserAction } from "@/lib/actions/auth";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { routes } from "@/lib/routes";
import { buildUrl } from "@/lib/utils/url";
import { registerSchema } from "@/lib/validations/auth";

// Define a new type that includes confirmPassword which is needed for the form
// but not included in the registerSchema
type RegisterFormData = z.infer<typeof extendedRegisterSchema>;

// Extend the register schema to include password confirmation
const extendedRegisterSchema = registerSchema
  .safeExtend({
    confirmPassword: registerSchema.shape.password,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

interface RegisterFormProps
  extends Omit<PageFormProps<RegisterFormData>, "onSubmit"> {
  registerUser: typeof registerUserAction;
  initialPlan?: string;
}

export function RegisterForm({
  initialPlan,
  registerUser,
  ...props
}: RegisterFormProps) {
  const [showOrganizationSearch, setShowOrganizationSearch] = useState(true);

  // Default values for the form
  const defaultValues: RegisterFormData = {
    confirmPassword: "",
    email: "",
    firstName: "",
    lastName: "",
    organizationName: "",
    password: "",
    phone: "",
  };

  // Form submission handler
  const handleFormSubmit = async (data: RegisterFormData) => {
    // Call the server action which returns an ActionResponse
    return await registerUser({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      organizationName: data.organizationName,
      password: data.password,
      planTier: initialPlan,
    });
  };

  // Error handler for validation errors
  const handleError = (error: Error) => {
    void EventPublisher.publishError(EventKeys.AUTH_ERROR, error, {
      flow: "register",
    });
  };

  // Handle organization search results
  const handleContinueWithNew = () => {
    setShowOrganizationSearch(false);
  };

  // Show organization search first
  if (showOrganizationSearch) {
    return (
      <div className="bg-default-50">
        <div className="w-full max-w-lg mx-auto py-16 px-4 sm:px-0">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold mb-2">
              Register Organization
            </h1>
            <p className="text-default-600">
              Before creating a new organization, let's check if your
              organization already exists on NeedBridge.
            </p>
          </div>

          <OrganizationSearch onContinueWithNew={handleContinueWithNew} />

          <div className="text-center text-sm mt-6">
            <span className="text-default-500">Already have an account? </span>
            <Link className="text-primary" href={routes.auth.login()}>
              Log in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageFormTemplate<RegisterFormData>
      callbackUrl={buildUrl(routes.auth.verifyEmail())}
      defaultValues={defaultValues}
      description="Create an organization admin account to manage your organization and invite team members"
      eventKeys={{
        attempt: EventKeys.AUTH_REGISTER_FORM_SUBMIT_ATTEMPT,
        failure: EventKeys.AUTH_REGISTER_FORM_SUBMIT_FAILURE,
        success: EventKeys.AUTH_REGISTER_FORM_SUBMIT_SUCCESS,
      }}
      formSchema={extendedRegisterSchema}
      submitLabel="Create Account"
      title="Register New Organization"
      onError={handleError}
      onSubmit={handleFormSubmit}
      {...props}
    >
      {({ control, formState: _formState }) => {
        return (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 mb-4">
              <p className="font-medium">⚠️ Creating New Organization</p>
              <p className="mt-1">
                You're about to create a new organization. Make sure this
                organization doesn't already exist on NeedBridge. If it does,
                you should contact the existing organization to join instead.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800 mb-4">
              <p className="font-medium">
                Organization Administrator Registration
              </p>
              <p className="mt-1">
                This form is only for registering as an organization
                administrator. After registration, you'll be able to invite case
                workers and coordinators to join your organization.
              </p>
            </div>

            <FormInput
              required
              control={control}
              label="First Name"
              name="firstName"
              placeholder="Enter your first name"
            />

            <FormInput
              required
              control={control}
              label="Last Name"
              name="lastName"
              placeholder="Enter your last name"
            />

            <FormInput
              required
              control={control}
              label="Your Email"
              name="email"
              placeholder="Enter your email address"
              type="email"
            />

            <FormInput
              control={control}
              label="Phone Number (Optional)"
              name="phone"
              placeholder="Enter your phone number"
              type="tel"
            />

            <FormInput
              required
              control={control}
              label="Organization Name"
              name="organizationName"
              placeholder="Enter your organization name"
            />

            <FormInput
              required
              control={control}
              label="Password"
              name="password"
              placeholder="Create a secure password"
              type="password"
            />

            <FormInput
              required
              control={control}
              label="Confirm Password"
              name="confirmPassword"
              placeholder="Confirm your password"
              type="password"
            />

            <div className="text-center text-sm space-y-2">
              <div>
                <button
                  className="text-amber-600 hover:text-amber-800 underline"
                  type="button"
                  onClick={() => setShowOrganizationSearch(true)}
                >
                  ← Back to Organization Search
                </button>
              </div>
              <div>
                <span className="text-default-500">
                  Already have an account?{" "}
                </span>
                <Link className="text-primary" href={routes.auth.login()}>
                  Log in
                </Link>
              </div>
            </div>
          </>
        );
      }}
    </PageFormTemplate>
  );
}
